import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  BidInput,
  PartnerRequest,
  completeRequest,
  getPartnerRequest,
  putBid,
  withdrawBid,
} from '../../api/marketplace';
import BidForm from './BidForm';
import {
  BID_STATUS_LABELS,
  URGENCY_LABELS,
  formatCountdown,
  formatCountries,
  formatDateTime,
  formatPrice,
  isBeforeDeadline,
  serviceLabel,
  statusLabel,
  useNow,
} from './marketplaceFormat';
import './Marketplace.css';

const PartnerRequestPage: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const { currentUser } = useAuth();
  const now = useNow();
  const [item, setItem] = useState<PartnerRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [completing, setCompleting] = useState(false);

  const load = useCallback(async () => {
    if (!currentUser || !requestId) return;
    setError('');
    try {
      const token = await currentUser.getIdToken();
      setItem(await getPartnerRequest(token, requestId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the request.');
    } finally {
      setLoading(false);
    }
  }, [currentUser, requestId]);

  useEffect(() => {
    load();
  }, [load]);

  const withToken = async <T,>(fn: (token: string) => Promise<T>): Promise<T> => {
    if (!currentUser) throw new Error('You are signed out.');
    return fn(await currentUser.getIdToken());
  };

  const submitBid = async (bid: BidInput) => {
    await withToken(token => putBid(token, requestId!, bid));
    await load();
  };

  const withdraw = async () => {
    await withToken(token => withdrawBid(token, requestId!));
    await load();
  };

  const markCompleted = async () => {
    if (!window.confirm('Mark this request as completed?')) return;
    setCompleting(true);
    setError('');
    try {
      await withToken(token => completeRequest(token, requestId!));
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark the request completed.');
    } finally {
      setCompleting(false);
    }
  };

  if (loading) return <p>Loading request...</p>;

  if (!item) {
    return (
      <div className="marketplace-page">
        <Link to="/marketplace" className="service-link">Back to the board</Link>
        <div className="status-error-banner" role="alert">{error || 'Request not found.'}</div>
      </div>
    );
  }

  const canBid = item.status === 'OPEN' && isBeforeDeadline(item.bidDeadline, now);
  const bid = item.myBid;

  return (
    <div className="marketplace-page">
      <Link to="/marketplace" className="service-link">Back to the board</Link>
      <div className="task-view-header">
        <h2>{serviceLabel(item.serviceType)}</h2>
        <span className={`marketplace-status marketplace-status--${item.status}`}>
          {statusLabel(item.status)}
        </span>
      </div>
      {error && <div className="status-error-banner" role="alert">{error}</div>}

      <section className="marketplace-panel">
        <dl className="marketplace-facts">
          <div><dt>Countries</dt><dd>{formatCountries(item.countries)}</dd></div>
          <div><dt>Language</dt><dd>{(item.language || 'n/a').toUpperCase()}</dd></div>
          <div><dt>Urgency</dt><dd>{URGENCY_LABELS[item.urgency] || item.urgency}</dd></div>
          <div>
            <dt>Bid deadline</dt>
            <dd>
              {formatDateTime(item.bidDeadline)}
              {item.status === 'OPEN' && (
                <span className="marketplace-countdown"> ({formatCountdown(item.bidDeadline, now)})</span>
              )}
            </dd>
          </div>
        </dl>
        <h3>Summary</h3>
        <p className="marketplace-summary marketplace-summary--full">{item.anonymisedSummary}</p>
      </section>

      {item.customer && (
        <section className="marketplace-panel marketplace-panel--won" aria-label="Customer contact">
          <h3>You won this request</h3>
          <p>Contact the customer to get started.</p>
          <dl className="marketplace-facts">
            <div><dt>Name</dt><dd>{item.customer.name || 'n/a'}</dd></div>
            <div>
              <dt>Email</dt>
              <dd>
                {item.customer.email ? (
                  <a href={`mailto:${item.customer.email}`} className="service-link">{item.customer.email}</a>
                ) : 'n/a'}
              </dd>
            </div>
            <div><dt>Language</dt><dd>{(item.customer.language || 'n/a').toUpperCase()}</dd></div>
          </dl>
          {item.status === 'AWARDED' && (
            <button type="button" className="download-button" onClick={markCompleted} disabled={completing}>
              Mark completed
            </button>
          )}
          {item.status === 'COMPLETED' && <p className="marketplace-hint">Marked completed.</p>}
        </section>
      )}

      {canBid ? (
        <section className="marketplace-panel">
          <BidForm existingBid={bid} onSubmit={submitBid} onWithdraw={withdraw} />
        </section>
      ) : bid ? (
        <section className="marketplace-panel" aria-label="Your bid">
          <h3>Your bid</h3>
          <dl className="marketplace-facts">
            <div><dt>Price</dt><dd>{formatPrice(bid.priceCents, bid.priceType)}</dd></div>
            <div><dt>ETA</dt><dd>{bid.etaDays} days</dd></div>
            <div><dt>Status</dt><dd>{BID_STATUS_LABELS[bid.status] || bid.status}</dd></div>
          </dl>
          {bid.message && <p className="marketplace-summary">{bid.message}</p>}
        </section>
      ) : (
        item.status === 'OPEN' && <p className="marketplace-hint">Bidding has closed for this request.</p>
      )}
    </div>
  );
};

export default PartnerRequestPage;
